import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  ElementRef,
  inject,
  input,
  NO_ERRORS_SCHEMA,
  OnInit,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { AuthService } from '../../core/services/auth-service';
import { User } from '../../core/models/user.model';
import { environment } from "../../../environments/environment";
import { isPlatformBrowser } from "@angular/common";
import { tenant } from '../../../tenants/tenant';

@Component({
  selector: 'app-nexa-chat',
  standalone: true,
  imports: [],
  templateUrl: './nexa-chat.component.html',
  styleUrl: './nexa-chat.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.standalone-mode]': 'standalone()' }
})
export class NexaChatComponent implements OnInit {
  private platformId = inject(PLATFORM_ID);
  authService = inject(AuthService);

  token = input<string | null>(null);
  intro = input<string>('');
  userType = input<string>('default');
  standalone = input<boolean>(false);
  subTenantId = input<string | null>(null);
  subTenantName = input<string | null>(null);

  isLoading = signal(true);

  private readonly chatConfig = tenant.chatConfig;
  private readonly chatEnv: string = (environment as any).envName === 'prod' ? 'prod' : 'uat';
  private readonly tenantAssistantName: string = this.chatConfig?.assistantName || 'MIA';
  private readonly tenantAssistantImage: string = this.chatConfig?.assistantImage || '';
  private readonly elementTag: string = this.chatConfig?.elementTag || 'nexa-chat';
  private readonly bundleFile: string = this.chatConfig?.bundleFile || 'nexa-chat.bundle.js';
  private readonly tenantEnvConfig = this.chatConfig?.envConfig ?? null;
  private readonly apiUrl: string = (environment as any).apiUrl ?? tenant.apiUrl;
  private readonly remoteUrl: string | undefined = this.chatEnv === 'prod'
    ? this.chatConfig?.remoteProdUrl
    : this.chatConfig?.remoteTestUrl;

  private chatEl: any = null;

  container = viewChild<ElementRef<HTMLElement>>('chatContainer');
  userInfo = computed<User | null>(() => this.authService.user() as User | null);

  constructor() {
    effect(() => {
      const token = this.token();
      if (this.chatEl && token != null) {
        this.chatEl.token = token;
      }
    });

    effect(() => {
      const intro = this.intro();
      const userName = this.userInfo()?.name ?? null;
      if (this.chatEl) {
        this.chatEl.intro = intro;
        this.chatEl.userName = userName;
      }
    });

    effect(() => {
      const subTenantId = this.subTenantId();
      const subTenantName = this.subTenantName();
      if (this.chatEl) {
        if (subTenantId) this.chatEl.subTenantId = subTenantId;
        if (subTenantName) this.chatEl.subTenantName = subTenantName;
      }
    });
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.initializeChatStrategy();
    }
  }

  private initializeChatStrategy() {
    if (this.chatConfig?.useRemoteLoader && this.remoteUrl) {
      const url = this.remoteUrl;
      console.log(`[Nexa] Loading Chat from CloudFront (${this.chatEnv}):`, url);
      this.loadRemoteScript(url);
    } else {
      console.log('[Nexa] Loading tenant bundle:', this.bundleFile);
      this.loadTenantBundle(this.bundleFile)
        .then(() => customElements.whenDefined(this.elementTag))
        .then(() => this.mountChatElement())
        .catch(err => console.error('[Nexa] Error loading bundle:', this.bundleFile, err));
    }
  }

  private loadTenantBundle(bundleFile: string): Promise<any> {
    switch (bundleFile) {
      case 'asrem-chat.bundle.js':
        // @ts-ignore
        return import('./asrem-chat.bundle.js');
      case 'unicatt-chat.bundle.js':
        // @ts-ignore
        return import('./unicatt-chat.bundle.js');
      case 'emergency-chat.bundle.js':
        // @ts-ignore
        return import('./emergency-chat.bundle.js');
      case 'lumia-chat.bundle.js':
        // @ts-ignore
        return import('./lumia-chat.bundle.js');
      case 'asl-salerno-chat.bundle.js':
        // @ts-ignore
        return import('./asl-salerno-chat.bundle.js');
      case 'nexa-chat.bundle.js':
      default:
        // @ts-ignore
        return import('./nexa-chat.bundle.js');
    }
  }

  private loadRemoteScript(url: string) {
    const scriptId = 'nexa-chat-script-remote';
    const existing = document.getElementById(scriptId);

    if (existing) {
      this.mountChatElement();
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = url;
    script.type = 'module';
    script.async = true;
    script.onload = () => {
      this.mountChatElement();
    };
    script.onerror = (err) => console.error('[Nexa] Failed to load chat bundle:', url, err);

    document.head.appendChild(script);
  }

  private mountChatElement() {
    const containerEl = this.container()?.nativeElement;
    if (!containerEl) {
      console.error('[Nexa] Chat container not found in DOM');
      return;
    }

    if (containerEl.querySelector(this.elementTag)) {
      console.log(`[Nexa] <${this.elementTag}> already mounted`);
      this.isLoading.set(false);
      return;
    }

    console.log(`[Nexa] Mounting <${this.elementTag}> (bundle: ${this.bundleFile})`);

    const el = document.createElement(this.elementTag) as any;

    el.env             = this.chatEnv;
    if (this.tenantEnvConfig) {
      // Merge per non perdere i campi (cognitoDomain, clientId, identityProvider, ...)
      // già definiti di default dal web component per ogni env.
      el.ENV_CONFIG = {
        ...el.ENV_CONFIG,
        uat: { ...el.ENV_CONFIG?.uat, ...this.tenantEnvConfig.uat },
        prod: { ...el.ENV_CONFIG?.prod, ...this.tenantEnvConfig.prod },
      };
    }
    if (this.apiUrl) {
      el.apiEndpoint = this.apiUrl;
    }
    el.assistantName   = this.tenantAssistantName;
    el.assistantSubtitle = 'Il tuo Assistente Virtuale';
    el.language        = 'it_IT';
    el.token           = this.token();
    el.intro           = this.intro();
    el.userType        = this.userType();
    el.standalone      = this.standalone();
    el.userName        = this.userInfo()?.name ?? null;
    el.hideDisclaimer  = true;

    const subTenantId = this.subTenantId();
    if (subTenantId) {
      el.subTenantId = subTenantId;
    }

    const subTenantName = this.subTenantName();
    if (subTenantName) {
      el.subTenantName = subTenantName;
    }

    const img = this.tenantAssistantImage;
    if (img && (img.startsWith('data:') || img.startsWith('http'))) {
      const imgEl = document.createElement('img');
      imgEl.setAttribute('slot', 'header-icon');
      imgEl.src = img;
      imgEl.alt = this.tenantAssistantName;
      imgEl.style.cssText = 'width:40px;height:40px;border-radius:50%;margin-right:12px;object-fit:cover;';
      el.appendChild(imgEl);
    }

    this.chatEl = el;
    containerEl.appendChild(el);
    this.isLoading.set(false);
  }

  showFeedback() {
    this.chatEl?.triggerFeedback();
  }
}
